import { RouterModule, Routes } from '@angular/router';
import { Page404Component } from './extrapages/page404/page404.component';
import { OrganizationSetupComponent } from './extrapages/organization-setup/organization-setup.component';
import { WebhookSetupComponent } from './extrapages/webhook-setup/webhook-setup.component';
import { WhatsappSetupComponent } from './extrapages/whatsapp-setup/whatsapp-setup.component';
import { BusinessDetailsSetupComponent } from './extrapages/business-details-setup/business-details-setup.component';
import { MessagingSetupComponent } from './extrapages/messaging-setup/messaging-setup.component';
import { AcceptInviteComponent } from './extrapages/accept-invite/accept-invite.component';
import { NoOrganizationComponent } from './extrapages/no-organization/no-organization.component';
import { LocationSetupComponent } from './extrapages/location-setup/location-setup.component';
import { AuthGuard } from './core/guards/auth.guard';
import { OrganizationGuard } from './core/guards/organization.guard';
import { onboardingGuard } from './core/guards/onboarding.guard';
import { pcoSetupGuard } from './core/guards/pco.guard';
import { supportModeGuard } from './core/guards/support-mode.guard';
import { conversationsOnlyGuard } from './core/guards/conversations-only.guard';
import { LayoutComponent } from './layouts/layout.component';
import { MobileLinkComponent } from './extrapages/mobile-link/mobile-link.component';

export const routes: Routes = [
    {
        path: "auth",
        loadChildren: () =>
            import("./account/account.module").then((m) => m.AccountModule),
    },
    {
        path: "organization-setup",
        component: OrganizationSetupComponent,
        canActivate: [AuthGuard], // Must be authenticated to set up organization
    },
    {
        // Planning Center webhook subscription. A standalone organisation's
        // step list already excludes it, so pcoSetupGuard only catches arrivals
        // that bypassed the wizard — a bookmark, or the back button.
        path: "webhook-setup",
        component: WebhookSetupComponent,
        // supportModeGuard as well: subscribing webhooks calls Planning Center
        // with the signed-in user's own token, which a support agent has not
        // got, so the page can only fail for them.
        canActivate: [AuthGuard, OrganizationGuard, pcoSetupGuard(), supportModeGuard()],
    },
    {
        // Country and timezone for organisations without Planning Center,
        // which supplies both. Only appears in their step list.
        path: "location-setup",
        component: LocationSetupComponent,
        canActivate: [AuthGuard, OrganizationGuard],
    },
    {
        path: "whatsapp-setup",
        component: WhatsappSetupComponent,
        canActivate: [AuthGuard, OrganizationGuard], // Setup step — accessible during onboarding
    },
    {
        path: "business-details-setup",
        component: BusinessDetailsSetupComponent,
        canActivate: [AuthGuard, OrganizationGuard], // Setup step — accessible during onboarding
    },
    {
        path: "messaging-setup",
        component: MessagingSetupComponent,
        canActivate: [AuthGuard, OrganizationGuard], // Setup step — accessible during onboarding
    },
    {
        // Deliberately unguarded. An invited person has no account yet, so
        // AuthGuard bounced them to a sign-in page that said nothing about the
        // invitation — and cleared the returnUrl carrying it out of the address
        // bar, which sent them to /auth/register to create an organisation of
        // their own. The page reads the invitation itself and offers sign-in in
        // context instead.
        path: "accept-invite",
        component: AcceptInviteComponent,
    },
    {
        // Dead end for a signed-in user with no organisation and no invitation.
        // Only AuthGuard — OrganizationGuard redirects here, so guarding it with
        // OrganizationGuard would loop.
        path: "no-organization",
        component: NoOrganizationComponent,
        canActivate: [AuthGuard],
    },
    {
        // Public legal pages. Deliberately unguarded and outside
        // LayoutComponent: App Store Connect requires the privacy policy URL as
        // submission metadata and App Review opens it directly, so a login wall
        // there is a rejection on its own. The mobile app's Settings screen
        // links to both, and congregants who received a message may follow them
        // without ever having an Echo account.
        //
        // Declared ABOVE the "" route on purpose. That route carries AuthGuard
        // and matches by prefix, so leaving these below it makes them depend on
        // the router failing to find a child match and backtracking — which is
        // exactly the kind of thing that quietly starts redirecting anonymous
        // visitors to the login screen.
        path: "privacy",
        loadComponent: () =>
            import("./extrapages/legal/privacy-policy.component").then(
                (m) => m.PrivacyPolicyComponent
            ),
    },
    {
        path: "terms",
        loadComponent: () =>
            import("./extrapages/legal/terms.component").then(
                (m) => m.TermsComponent
            ),
    },
    {
        // Media requirements for WhatsApp uploads. Public for the same reasons
        // as the legal pages above, plus one of its own: it is opened in a new
        // tab from inside the create-template modal, and clients forward it to
        // whoever actually edits their video — who has no Echo login.
        //
        // Deep-linked from a rejected upload with ?format= and ?reason=, so the
        // relevant fix is the first thing on screen.
        path: "help/media-requirements",
        loadComponent: () =>
            import("./extrapages/help/media-requirements.component").then(
                (m) => m.MediaRequirementsHelpComponent
            ),
    },
    {
        // Support URL from the App Store listing. Same reasoning as above: it
        // is opened during review, and the people who need it most are the ones
        // who cannot sign in.
        path: "support",
        loadComponent: () =>
            import("./extrapages/legal/support.component").then(
                (m) => m.SupportComponent
            ),
    },
    {
        // Entry point for a Solenex support session, linked from the platform
        // console on admin.echo.solenex.it.
        //
        // The console cannot start the session itself any more. Firebase Auth
        // sessions are per-origin, so an administrator signed in on the console
        // is anonymous here — and worse, the beforeSignIn blocking function
        // deliberately ends any open support session on a fresh sign-in, so
        // starting one on the console and then signing in here would silently
        // tear it straight back down. Landing here first means AuthGuard settles
        // the sign-in before impersonation begins.
        //
        // A sibling of the root route below, not a child: that route carries
        // OrganizationGuard, and an administrator between sessions has no
        // organisation of their own, so it would bounce them to /auth/register.
        // Deliberately unguarded — no AuthGuard.
        //
        // This is where a Solenex support agent lands from the console, and
        // they are anonymous on this origin: Firebase Auth sessions do not
        // cross one. A guard here would send the one person the page exists
        // for to a login screen for a customer account they have no reason to
        // own, which is exactly what it used to do. The one-time code in the
        // URL is what authorises this, and it is verified server-side by
        // adminRedeemSupportHandoff, which re-checks the allowlist before
        // minting anything.
        path: "support-session",
        loadComponent: () =>
            import("./extrapages/support-session/support-session.component").then(
                (m) => m.SupportSessionComponent
            ),
    },
    {
        path: "",
        component: LayoutComponent,
        loadChildren: () =>
            import("./pages/pages.module").then((m) => m.PagesModule),
        canActivate: [AuthGuard, OrganizationGuard, onboardingGuard], // Must complete onboarding
        // Support Staff (the `support` role, not a Solenex support session)
        // may only open Conversations. On the children rather than here, so
        // the "" -> dashboard redirect is caught too.
        canActivateChild: [conversationsOnlyGuard],
    },
    {
        path: "pages",
        loadChildren: () =>
            import("./extrapages/extrapages.module").then((m) => m.ExtrapagesModule),
        canActivate: [AuthGuard, OrganizationGuard, onboardingGuard], // Must complete onboarding
    },
    {
        // Links intended for the mobile app. The app claims /m/* as a Universal
        // Link / App Link, so this only renders when it could not take over:
        // desktop, a phone without the app, or a URL pasted into a browser.
        // Without it those all hit the 404 below, which on a sign-in link
        // someone is actively using reads as a broken product.
        //
        // Deliberately unguarded — the whole point is that the person is not
        // signed in yet.
        path: "m/email-signin",
        component: MobileLinkComponent
    },
    { path: "**", component: Page404Component },
];
